import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Form from '../Form';
import ApiService from '../../../services/ApiService';
import { modal } from '../../modal/ModalEmitter';
import toastr from '../../toastr';
import LoadingPage from '../../LoadingPage';
import FileNotFound from '../../FileNotFound';

const PrintHardCard = ({ roId }) => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [remedyCodeAttachments, setRemedyCodeAttachments] = useState([]);
  const [roAttachments, setROAttachments] = useState([]);
  const [printers, setPrinters] = useState([]);

  const getLayout = () => {
    const layout = [
      {
        name: 'printer',
        label: 'Printer',
        noEmptyOption: true,
        options: printers,
        required: true,
      },
      {
        name: 'operations',
        label: 'Operations to Print',
      },
    ];

    const options = [];
    for (const remedyCodeAttachment of remedyCodeAttachments) {
      remedyCodeAttachment.attachments.forEach(attachment => {
        options.push({
          label: attachment.filename,
          value: `${attachment.key}-${attachment.filename}`,
        });
      });
    }

    if (options.length) {
      layout.push({
        control: 'ReactSelect',
        name: 'attachments',
        label: 'Remedy Code Attachments',
        options,
        data: options.map(o => o.value),
        inputProps: {
          isMulti: true,
        },
        helpText: 'Attached files will be printed',
      });
    }

    const roAttachmentOptions = [];
    for (const attachment of roAttachments) {
      roAttachmentOptions.push({
        label: attachment.filename,
        value: `${attachment.key}-${attachment.filename}`,
      });
    }

    if (roAttachmentOptions.length) {
      layout.push({
        control: 'ReactSelect',
        name: 'roAttachments',
        label: 'Repair Order Attachments',
        options: roAttachmentOptions,
        data: [],
        inputProps: {
          isMulti: true,
        },
        helpText: 'Attached files will be printed',
      });
    }

    return layout;
  };

  const submit = payload => {
    return ApiService.post(`/repair/${encodeURIComponent(roId)}/print-hard-card`, payload).then(response => {
      // Form submit handler should do this portion already
      // const message = _.get(response, 'data.responses[0].message');
      // if (/^https?:\/\//.test(message)) {
      //   window.open(message, '_blank');
      // }
      toastr.success('Print Hard Card', `${roId} (${payload.operations || 'All'} Ops) sent to ${payload.printer}`);
      return response;
    });
  };

  const getRemedyCodeAttachments = () => {
    return ApiService.get(`/repair/${encodeURIComponent(roId)}/remedy-code-attachments`).then(response => {
      setRemedyCodeAttachments(response.data);
    });
  };

  const getROAttachments = () => {
    return ApiService.get(`/repair/${encodeURIComponent(roId)}/attachments`).then(response => {
      setROAttachments(response.data);
    });
  };

  const getPrinters = () => {
    const params = {
      repairOrderId: encodeURIComponent(roId),
      isOpen: true,
    };

    return ApiService.get('/printers/ro', { params }).then(response => {
      const { printers, error } = response.data;

      if (error) {
        setErrorMessage(error);
        return;
      }

      setPrinters(printers);
    });
  };

  useEffect(() => {
    if (errorMessage) {
      modal.close();
      modal.alert({ title: 'Notice', message: errorMessage });
    }
  }, [errorMessage]);

  useEffect(() => {
    Promise.all([getPrinters(), getRemedyCodeAttachments(), getROAttachments()]).then(() => {
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <LoadingPage style={{ height: '195px' }} />;
  }

  if (errorMessage) {
    return <FileNotFound>{errorMessage}</FileNotFound>;
  }

  return (
    <Form
      submitAll
      layout={getLayout()}
      onCancel={() => modal.close()}
      submit={submit}
      submitCallback={() => modal.close()}
      exportFormat="object"
      feedback="erroronly"
      trackDirty={false}
      useDirtyWarning={false}
    />
  );
};

PrintHardCard.propTypes = {
  roId: PropTypes.string,
};

export default PrintHardCard;
